import {
  socialAuthError,
  socialAuthSuccess,
  socialLoginClicked,
} from '@wix/bi-logger-hls2/v2';
import { action, computed, makeObservable, observable } from 'mobx';
import { ROUTES } from '../routes';
import { ERROR_CODES, extractServerErrorCode } from '../utils/errorHandler';
import { RootStore } from './root';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import { AuthRequest } from '../types';
import { handleSsoRedirect } from '../utils/handleSsoRedirect';
import { createBiLogger } from '../services/biLogger';
import { IDP_POPUP_BI_VERSION } from '../utils/constants';
import {
  authMethodFromProviderId,
  setLastUsedAuthMethod,
} from '../utils/lastUsedAuthMethod';
import {
  lastUsedBiView,
  lastUsedTransactionData,
} from '../utils/lastUsedAuthMethodBi';

const MAX_ERROR_REASON_LENGTH = 500;
const UNKNOWN_ERROR_REASON = 'Unknown error';

/**
 * Turns whatever a social SDK rejected with into a BI `reason` string. gapi
 * rejects with objects like `{ error: 'popup_closed_by_user' }`, which would
 * otherwise be reported as "[object Object]".
 */
export function normalizeErrorReason(error: unknown): string {
  let reason: string | undefined;
  if (typeof error === 'string') {
    reason = error;
  } else if (error && typeof error === 'object') {
    const { error: innerError, message } = error as {
      error?: unknown;
      message?: unknown;
    };
    const candidate = innerError ?? message;
    if (typeof candidate === 'string') {
      reason = candidate;
    } else {
      try {
        reason = JSON.stringify(candidate ?? error);
      } catch {
        reason = undefined;
      }
    }
  } else if (error !== undefined && error !== null) {
    reason = String(error);
  }
  return (reason || UNKNOWN_ERROR_REASON).slice(0, MAX_ERROR_REASON_LENGTH);
}

export abstract class SocialProviderLoginStore<T> {
  protected readonly rootStore: RootStore;
  abstract readonly providerId: string;
  protected abstract readonly idpConnectionId: string;
  protected abstract readonly serverOAuthSpec: string;
  customButtonLogoUrl: string;
  customIconLogoUrl: string;
  lastLoginAuthParams: T;
  hasError: boolean = false;
  errorCode: string = '';
  isLoading: boolean = false;
  isInitialized: boolean = false;
  useNewSdk: boolean = false;
  abstract init(): void;
  abstract initializeSdk(options?: { [key: string]: string }): void;
  abstract onLogin(authResult: any): void;
  abstract onClickCustomLoginButton(): void;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      hasError: observable,
      errorCode: observable,
      isLoading: observable,
      isInitialized: observable,
      lastLoginAuthParams: observable,
      handleError: action,
      shouldUseIdpPopup: computed,
      loginWithIdpPopup: action,
    });
  }

  get shouldUseIdpPopup(): boolean {
    return this.rootStore.experiments?.enabled(this.serverOAuthSpec);
  }

  /**
   * "Last used" experiment attribution for this provider, for the `view`
   * field. Emitted in both variants, so variant A is the control population
   * rather than a blank.
   */
  private get lastUsedView(): string {
    return lastUsedBiView(
      this.rootStore.experiments,
      authMethodFromProviderId(this.providerId),
    );
  }

  /** The same attribution as flat fields, for Panorama transactions. */
  private get lastUsedPanoramaData() {
    return lastUsedTransactionData(
      this.rootStore.experiments,
      authMethodFromProviderId(this.providerId),
    );
  }

  async loginWithIdpPopup(): Promise<{ success: boolean; errorCode?: string }> {
    this.rootStore.webauthnStore.abortWebAuthnAuthentication();
    this.socialButtonClicked({ version: IDP_POPUP_BI_VERSION });
    return this.rootStore.idpConnectionLoginStore.loginWithIdpConnection(
      this.idpConnectionId,
      this.rootStore.sessionId.get(),
    );
  }

  protected handleIdpPopupClick(): void {
    this.clearErrors();
    this.loginWithIdpPopup()
      .then(({ success, errorCode }) => {
        // The IDP popup flow records its error on idpConnectionLoginStore, but
        // the inline social error is rendered from this per-provider store.
        // Mirror it over so the message surfaces on the login screen. Branches
        // that navigate away (blocked account, get-email, etc.) leave hasError
        // false and are skipped here.
        const idpStore = this.rootStore.idpConnectionLoginStore;
        if (!success && idpStore.hasError) {
          this.hasError = true;
          this.errorCode = errorCode ?? idpStore.errorCode;
        }
      })
      .catch((error) =>
        this.handleError({
          error,
          showErrorStatus: false,
          version: IDP_POPUP_BI_VERSION,
        }),
      );
  }

  handleError(
    options: {
      error?: unknown;
      state?: string;
      showErrorStatus?: boolean;
      version?: string;
    } = {},
  ) {
    const { error, state, showErrorStatus = true, version } = options;
    this.hasError = showErrorStatus;
    this.rootStore.biLogger.report(
      socialAuthError({
        socialNetwork: this.providerId,
        reason: normalizeErrorReason(error),
        state,
        view: this.lastUsedView,
        ...(version ? { version } : {}),
      }),
    );
  }

  clearErrors() {
    this.hasError = false;
    this.errorCode = '';
  }

  private handleLoginToWixSuccess(
    response: any,
    extendedData: { [key: string]: string } = {},
  ) {
    this.rootStore.biLogger = createBiLogger();
    this.rootStore.postLoginStore.biLogger = createBiLogger();
    this.rootStore.biLogger.report(
      socialAuthSuccess({
        socialNetwork: this.providerId,
        context: this.rootStore.emailStepStore.getContext(),
        view: this.lastUsedView,
        ...(extendedData.authMethod === 'GOOGLE_ONE_TAP'
          ? { state: 'googleOT' }
          : {}),
      }),
    );
    // Remember the provider for the "last used" badge. This sits on the
    // success path only (register included — the next visit is a login), so a
    // failed or abandoned attempt never moves the badge.
    const lastUsedMethod = authMethodFromProviderId(this.providerId);
    if (lastUsedMethod) {
      setLastUsedAuthMethod(lastUsedMethod);
    }
    const headerRedirectUrl = response.headers['x-wix-redirect-url'];
    if (headerRedirectUrl) {
      this.rootStore.navigationStore.redirect(headerRedirectUrl);
      return;
    }
    const redirectParams = { ...response.data?.payload, ...extendedData };
    if (response.data?.action === 'register') {
      // TODO - Show post signup modal if requested
      return this.rootStore.navigationStore.postSignup(redirectParams);
    }
    this.rootStore.navigationStore.postLogin(redirectParams);
  }

  isValidInteraction(errorCode: string = '') {
    return [
      ERROR_CODES.SOCIAL_LOGIN_VERIFY_ACCOUNT,
      ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL,
      ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR,
      ERROR_CODES.USER_DELETED_OR_BLOCKED,
      ERROR_CODES.USER_IS_DELETED,
      ERROR_CODES.USER_IS_BLOCKED,
      ERROR_CODES.OFAC_COUNTRIES_LOCATION_ERROR,
      ERROR_CODES.INVALID_EMAIL_OR_PASSWROD,
      ERROR_CODES.BLOCKED_DOMAIN,
    ].includes(errorCode);
  }

  socialButtonClicked({ version }: { version?: string } = {}) {
    this.rootStore.biLogger.report(
      socialLoginClicked({
        socialNetwork: this.providerId,
        context: this.rootStore.emailStepStore.getContext(),
        view: this.lastUsedView,
        ...(version ? { version } : {}),
      }),
    );
  }

  private async handleLoginToWixFailure(
    interactionName: string,
    data: any = {},
  ): Promise<{ errorCode?: string }> {
    this.rootStore.biLogger.report(
      socialAuthError({
        socialNetwork: this.providerId,
        reason: data.errorCode,
        view: this.lastUsedView,
      }),
    );
    const navigate = this.rootStore.navigationStore.navigate;
    const errorCode = extractServerErrorCode(data);
    if (this.isValidInteraction(errorCode)) {
      this.rootStore.panorama.finishTransaction(
        interactionName,
        this.lastUsedPanoramaData,
      );
    }
    if (errorCode === ERROR_CODES.SOCIAL_LOGIN_VERIFY_ACCOUNT) {
      this.rootStore.setActiveSocialProvider(this.providerId);
      // Check if user should be redirected to SSO instead of account verification
      const ssoRedirected = await handleSsoRedirect(
        this.rootStore,
        this.rootStore.userDataStore.email,
      );
      // Default behavior: show account verification only if no SSO redirect happened
      if (!ssoRedirected) {
        if (!this.rootStore.userDataStore.email) {
          this.rootStore.userDataStore.setEmail(data.errorDescription);
        }
        navigate(ROUTES.ACCOUNT_VERIFICATION);
      }
    } else if (errorCode === ERROR_CODES.USER_IS_BLOCKED) {
      this.rootStore.navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.SOCIAL,
      });
    } else if (errorCode === ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL) {
      this.rootStore.setActiveSocialProvider(this.providerId);
      navigate(ROUTES.SOCIAL_ENTER_EMAIL);
    } else if (errorCode === ERROR_CODES.BLOCKED_DOMAIN) {
      // Email domain is not allowed for social login (e.g., Wix employees)
      this.hasError = true;
      this.errorCode = errorCode;
    } else if (errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR) {
      const { accountSsoLoginUrl } = data.payload;
      navigate(accountSsoLoginUrl);
    } else if (errorCode === ERROR_CODES.EDITOR_X_SIGNUP_DISABLED_ERROR) {
      // Show Editor X signup disabled modal
      this.rootStore.socialAuthStore.showEditorXSignupDisabledModal = true;
    } else {
      this.hasError = true;
      this.errorCode = errorCode;
    }

    // The expected errors here are -
    // USER_DELETED_OR_BLOCKED, OFAC_COUNTRY, INVALID_EMAIL_OR_PASSWROD

    return { errorCode };
  }

  async loginToWix(
    authParams: T,
    extendedData: { [key: string]: string } = {},
  ): Promise<{ errorCode?: string; success: boolean }> {
    // Stop any pending usernameless passkey get() before starting social auth.
    this.rootStore.webauthnStore.abortWebAuthnAuthentication();
    this.lastLoginAuthParams = authParams;
    this.isLoading = true;
    this.hasError = false;
    try {
      const interactionName = `${this.providerId}-server-response`;
      // Timing is already measured here; the attribution is what makes the
      // "time to successful login" KPI sliceable by variant.
      const lastUsedData = this.lastUsedPanoramaData;
      this.rootStore.panorama.startTransaction(interactionName, lastUsedData);
      const response = await this.rootStore.apiStore.socialLogin(
        this.rootStore.apiStore.applySocialLoginParams(
          authParams as AuthRequest,
        ),
        this.providerId,
      );
      if (response.data?.success) {
        this.rootStore.panorama.finishTransaction(
          interactionName,
          lastUsedData,
        );
        this.handleLoginToWixSuccess(response, extendedData);
        return { success: true };
      } else {
        const email = this.extractEmailFromIdToken(
          (authParams as any)?.id_token || (authParams as any)?.identityToken,
        );
        if (email) {
          this.rootStore.userDataStore.setEmail(email);
        }
        this.isLoading = false;
        return {
          ...(await this.handleLoginToWixFailure(
            interactionName,
            response.data,
          )),
          success: false,
        };
      }
    } catch (error) {
      this.isLoading = false;
      return {
        errorCode: extractServerErrorCode(error as any),
        success: false,
      };
    }
  }

  continueLoginToWix(options: { password?: string } = {}) {
    return this.loginToWix({ ...this.lastLoginAuthParams, ...options });
  }

  extractEmailFromIdToken(idToken: string): string | null {
    try {
      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = idToken.split('.');
      if (parts.length !== 3) {
        return null;
      }
      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '==='.slice((payload.length + 3) % 4);
      // Decode base64 and parse JSON
      const decodedPayload = JSON.parse(atob(paddedPayload));
      // Extract email from the payload
      return decodedPayload.email || null;
    } catch (error) {
      return null;
    }
  }
}
